import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Wrench, Plus, Trash2, AlertCircle, CheckCircle, Info, Loader2 } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, autoQuoteService } from '../services/api';
import { VehicleType } from '../types';
import type { WorkshopStandardServiceDto } from '../types';

// ─────────────────────────────────────────────
//  WORKSHOP STANDARD SERVICES
//
//  The catalogue a workshop opts into auto-quoting. Matching is exact on the service
//  code the customer picked when posting the repair — never on the free-text fault
//  description, and never by AI.
//
//  A code with no row here simply is not auto-quoted, which is the correct failure
//  direction: pricing an unseen fault at a firm figure is a dispute waiting to happen.
// ─────────────────────────────────────────────

/**
 * Mirrors REPAIR_SERVICE_TYPES on the post-a-repair form. Only the services that can honestly
 * be priced sight-unseen are offered here — "General Repairs", "Diagnostics" and "Bodywork"
 * are deliberately absent, because nobody can quote those without looking at the vehicle.
 */
const QUOTABLE_SERVICES: { value: string; label: string }[] = [
  { value: 'mot',            label: 'MOT' },
  { value: 'annual_service', label: 'Annual Service / Inspection' },
  { value: 'servicing',      label: 'Servicing' },
  { value: 'tyres',          label: 'Tyres' },
];

const VEHICLE_OPTIONS: { label: string; value: VehicleType | '' }[] = [
  { label: 'Any vehicle', value: '' },
  { label: 'Car',         value: VehicleType.Car },
  { label: 'Small Van',   value: VehicleType.SmallVan },
  { label: 'LWB Van',     value: VehicleType.LWBVan },
  { label: 'Luton Van',   value: VehicleType.LutonVan },
  { label: '7.5T',        value: VehicleType.Tonne7_5 },
  { label: '18T',         value: VehicleType.Tonne18 },
];

const money = (n: number) => `£${n.toFixed(2)}`;

function labelFor(code: string) {
  return QUOTABLE_SERVICES.find(s => s.value === code)?.label ?? code;
}

function vehicleLabel(vt: VehicleType | null | undefined) {
  if (vt === null || vt === undefined) return 'Any vehicle';
  return VEHICLE_OPTIONS.find(v => v.value === vt)?.label ?? `Type ${vt}`;
}

export default function WorkshopServicesPage() {
  const { getToken } = useAppAuth();
  const client = useMemo(() => createApiClient(getToken), [getToken]);
  const svc    = useMemo(() => autoQuoteService(client), [client]);
  const qc     = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data: services = [], isLoading } = useQuery<WorkshopStandardServiceDto[]>({
    queryKey: ['auto-quote', 'standard-services'],
    queryFn : svc.getStandardServices,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['auto-quote', 'standard-services'] });

  const saveMutation = useMutation({
    mutationFn: svc.saveStandardService,
    onSuccess: () => {
      setError(null);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
      void refresh();
    },
    onError: (err: { response?: { data?: { message?: string; detail?: string } } }) =>
      setError(err?.response?.data?.message ?? err?.response?.data?.detail ?? 'Could not save that service.'),
  });

  const deleteMutation = useMutation({
    mutationFn: svc.deleteStandardService,
    onSuccess: () => void refresh(),
  });

  // ── New row ───────────────────────────────────────────────────────────────
  const [code, setCode]         = useState(QUOTABLE_SERVICES[0].value);
  const [vehicle, setVehicle]   = useState<VehicleType | ''>('');
  const [hours, setHours]       = useState(1);
  const [rate, setRate]         = useState(60);
  const [parts, setParts]       = useState(0);
  const [enabled, setEnabled]   = useState(true);

  const previewTotal   = hours * rate + parts;
  const previewLabour  = hours * rate;

  const add = () => saveMutation.mutate({
    serviceCode: code,
    vehicleType: vehicle === '' ? null : vehicle,
    labourHours: hours,
    labourRate:  rate,
    partsAmount: parts,
    isAutoQuoteEnabled: enabled,
  });

  const toggle = (s: WorkshopStandardServiceDto) => saveMutation.mutate({
    serviceCode: s.serviceCode,
    vehicleType: s.vehicleType,
    labourHours: s.labourHours,
    labourRate:  s.labourRate,
    partsAmount: s.partsAmount,
    isAutoQuoteEnabled: !s.isAutoQuoteEnabled,
  });

  if (isLoading) {
    return (
      <div className="p-8 flex items-center gap-2 text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your services…
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4" data-testid="workshop-services-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Wrench className="w-6 h-6 text-blue-600" /> Standard Services
        </h1>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          Set your price for the jobs you can quote without seeing the vehicle. When a customer
          posts one of these, we quote for you automatically.
        </p>
      </div>

      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-800">
          Only these four services can be auto-quoted. Anything needing a look first — general
          repairs, diagnostics, bodywork — always comes to you as a normal job to price yourself.
          The platform fee is charged on <strong>labour only</strong>, never on parts.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2" role="alert">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {saved && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <p className="text-sm text-emerald-700">Saved.</p>
        </div>
      )}

      {/* ── Existing ── */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900 text-sm">Your services</h2>
        </div>

        {services.length === 0 ? (
          <p className="px-5 py-8 text-sm text-gray-400 italic text-center">
            No services yet. Add one below and it&apos;ll be quoted automatically.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="workshop-services-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 bg-gray-50">
                  <th className="px-5 py-2 font-medium">Service</th>
                  <th className="px-3 py-2 font-medium">Vehicle</th>
                  <th className="px-3 py-2 font-medium text-right">Labour</th>
                  <th className="px-3 py-2 font-medium text-right">Parts</th>
                  <th className="px-3 py-2 font-medium text-right">Total</th>
                  <th className="px-3 py-2 font-medium">Auto-quote</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {services.map(s => (
                  <tr key={s.serviceId} className="border-t border-gray-100">
                    <td className="px-5 py-2.5 font-medium text-gray-900">{labelFor(s.serviceCode)}</td>
                    <td className="px-3 py-2.5 text-gray-500">{vehicleLabel(s.vehicleType)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">
                      {s.labourHours}h × {money(s.labourRate)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{money(s.partsAmount)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-gray-900">
                      {money(s.labourHours * s.labourRate + s.partsAmount)}
                    </td>
                    <td className="px-3 py-2.5">
                      <button
                        onClick={() => toggle(s)}
                        data-testid={`workshop-service-toggle-${s.serviceCode}`}
                        className={`px-2 py-1 rounded-full text-[11px] font-semibold ${
                          s.isAutoQuoteEnabled
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {s.isAutoQuoteEnabled ? 'On' : 'Off'}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        onClick={() => deleteMutation.mutate(s.serviceId)}
                        aria-label={`Remove ${labelFor(s.serviceCode)}`}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Add ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-900 text-sm mb-3">Add a service</h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Service</span>
            <select
              value={code} onChange={e => setCode(e.target.value)}
              data-testid="workshop-service-code"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              {QUOTABLE_SERVICES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">Vehicle class</span>
            <select
              value={vehicle}
              onChange={e => setVehicle(e.target.value === '' ? '' : parseInt(e.target.value, 10) as VehicleType)}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              {VEHICLE_OPTIONS.map(v => <option key={String(v.value)} value={v.value}>{v.label}</option>)}
            </select>
            <span className="text-[11px] text-gray-400 mt-1 block">
              A class-specific price beats an &ldquo;any vehicle&rdquo; one.
            </span>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">Labour hours</span>
            <input
              type="number" step="0.25" min="0" value={hours}
              onChange={e => setHours(parseFloat(e.target.value) || 0)}
              data-testid="workshop-service-hours"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">Labour rate (£/hour)</span>
            <input
              type="number" step="0.01" min="0" value={rate}
              onChange={e => setRate(parseFloat(e.target.value) || 0)}
              data-testid="workshop-service-rate"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">Parts (£)</span>
            <input
              type="number" step="0.01" min="0" value={parts}
              onChange={e => setParts(parseFloat(e.target.value) || 0)}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </label>

          <label className="flex items-end gap-2 pb-2 cursor-pointer">
            <input
              type="checkbox" checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600"
            />
            <span className="text-sm text-gray-700">Auto-quote this</span>
          </label>
        </div>

        {/* What the customer would actually be quoted, and what reaches the workshop. */}
        <div className="mt-4 bg-gray-50 rounded-lg px-4 py-3 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
          <span className="text-gray-500">You would quote</span>
          <span className="font-bold text-gray-900 tabular-nums" data-testid="workshop-service-preview">
            {money(previewTotal)}
          </span>
          <span className="text-gray-400 text-xs">
            labour {money(previewLabour)} + parts {money(parts)} — the platform fee applies to
            the labour only
          </span>
        </div>

        <button
          onClick={add}
          disabled={saveMutation.isPending || hours <= 0}
          data-testid="workshop-service-add"
          className="mt-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-5 py-2.5 rounded-lg text-sm flex items-center gap-2"
        >
          {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add service
        </button>
        {hours <= 0 && (
          <p className="text-[11px] text-gray-400 mt-2">
            Labour hours must be above zero — otherwise the quote would be the parts price alone.
          </p>
        )}
      </div>
    </div>
  );
}
